import { portalText } from "../../../i18n/portal.js";
/**
 * The opening hours as a week, and the one sentence the build reads — customer-portal review #5
 * (fix round D, 2026-09-25).
 *
 * The field was a free-text box: "Δευτέρα–Κυριακή 10:00–02:00" and "none" were both accepted, and
 * the build only said what it could not read after the agent existed. The wizard now edits a week —
 * each day closed, or open for one or two periods — and writes it in the grammar
 * `apps/factory/src/build/draft-input.ts` documents, so what it sends is always readable:
 *
 * ```
 *   Mon-Fri 12:00-23:30; Sat, Sun 13:00-01:00
 *   Tue-Sun 11:30-15:00, 18:00-23:00
 * ```
 *
 * Days with the same periods share a clause, runs of three or more are written as a range, and a day
 * no clause names is closed — the factory's own reading. A closing time earlier than the opening
 * time runs past midnight. The times are in the workspace's time zone, as the build reads them.
 *
 * A day open the whole day (review 76, fix round R) is the period `00:00` to `24:00`
 * ({@link ALL_DAY_PERIOD}, set by the editor's "All day" box; `00:00` to `00:00` typed into the time
 * boxes means the same) and is written `00:00-24:00`, the grammar's own form for the whole day.
 *
 * AMENDED 2026-10-09 (plan-015 AGC-UI, gaps G4, G8, G12): the times are in the VoiceAgent's OWN time zone now (owner
 * answer 12; the workspace's until changed), and the module also holds the rules of the other answering answers — the
 * special dates (chips, the add row, consecutive dates sharing a chip) and the out-of-hours and busy choices with their
 * copy — which the wizard's Hours step and the agent page's "When it answers" card both read.
 */
import { agentTemplateOf, answersOutsideOpeningHours, engineCapabilityOf, forwardDestinationRefusalOf } from "@wetalk/domain";

import { talkTimeText } from "../../../talk-time.js";

/** One period a day is open: `HH:MM` to `HH:MM`, as `<input type="time">` writes them. */
export interface OpeningPeriod {
  readonly opensAt: string;
  readonly closesAt: string;
}

/** Monday first, seven entries; an empty list is a closed day. */
export type OpeningWeek = readonly (readonly OpeningPeriod[])[];

export const DAY_NAME = () => ([portalText("operations", "monday_932eeb10"), portalText("operations", "tuesday_42e43b61"), portalText("operations", "wednesday_5656b9b7"), portalText("operations", "thursday_76031ddf"), portalText("operations", "friday_d166e844"), portalText("operations", "saturday_17063c50"), portalText("operations", "sunday_bc5dd045")] as const);

/** The abbreviations the factory's grammar reads (`DAY_NAME` in draft-input.ts), and the week editor's row labels. */
export const DAY_SHORT = () => ([portalText("operations", "mon_24b2a099"), portalText("operations", "tue_529541bb"), portalText("operations", "wed_23408b19"), portalText("operations", "thu_3593ccd9"), portalText("operations", "fri_bbd6e32e"), portalText("operations", "sat_6b782d41"), portalText("operations", "sun_48c98cab")] as const);

/** At most this many periods a day in the edit panel's editor: a lunch and a dinner service. */
export const MAX_PERIOD_PER_DAY = 2;

/**
 * At most this many shifts a day in the wizard's week editor (design sync 2026-10-09: "Add hours" hides at three). The
 * factory's grammar takes any number of periods a day; a stored draft is read back up to this many.
 */
export const MAX_SHIFT_PER_DAY = 3;

/** The whole day: what the editor's "All day" box sets, written `00:00-24:00` for the build. */
export const ALL_DAY_PERIOD: OpeningPeriod = { opensAt: "00:00", closesAt: "24:00" };

/** `true` for the whole day: the "All day" box, or `00:00` to `00:00` typed into the time boxes. */
export function isAllDay(period: OpeningPeriod): boolean {
  return period.opensAt === "00:00" && (period.closesAt === "24:00" || period.closesAt === "00:00");
}

/** One period in the grammar: `12:00-23:30`, or `00:00-24:00` for the whole day. */
function periodText(period: OpeningPeriod): string {
  return isAllDay(period) ? "00:00-24:00" : `${period.opensAt}-${period.closesAt}`;
}

export function closedWeek(): OpeningWeek {
  return DAY_NAME().map(() => []);
}

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * What is wrong with the week, in words, or `null`. Each problem names its day, so the person
 * finds it without reading the whole week.
 */
export function openingWeekProblem(week: OpeningWeek): string | null {
  if (week.length !== DAY_NAME().length) return portalText("operations", "set_the_hours_for_each_day_of_the_week_a6230fc5");
  for (const [index, period] of week.entries()) {
    const day = DAY_NAME()[index] as string;
    for (const one of period) {
      if (isAllDay(one)) {
        if (period.length > 1) return portalText("operations", "value_a_day_open_all_day_has_no_second_period_remove_it_or_4d87d6b8", { v0: String(day) });
        continue;
      }
      if (!TIME.test(one.opensAt) || !TIME.test(one.closesAt)) {
        return portalText("operations", "value_set_both_the_opening_and_the_closing_time_or_mark_th_c20fa5c4", { v0: String(day) });
      }
      if (one.opensAt === one.closesAt) {
        return portalText("operations", "value_it_opens_and_closes_at_the_same_time_to_stay_open_al_ec011f0f", { v0: String(day) });
      }
    }
  }
  if (week.every((period) => period.length === 0)) {
    return portalText("operations", "open_at_least_one_day_callers_outside_these_hours_hear_tha_fb0d1e15");
  }
  return null;
}

function sameDay(one: readonly OpeningPeriod[], other: readonly OpeningPeriod[]): boolean {
  return one.map(periodText).join(", ") === other.map(periodText).join(", ");
}

/** The days of one clause, in the grammar: runs of three or more as a range, the rest listed. */
const GRAMMAR_DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

function dayListOf(index: readonly number[], names: readonly string[] = GRAMMAR_DAY_SHORT): string {
  const part: string[] = [];
  let at = 0;
  while (at < index.length) {
    let end = at;
    while (end + 1 < index.length && (index[end + 1] as number) === (index[end] as number) + 1) end += 1;
    const first = names[index[at] as number] as string;
    const last = names[index[end] as number] as string;
    if (end - at >= 2) part.push(`${first}-${last}`);
    else for (let each = at; each <= end; each += 1) part.push(names[index[each] as number] as string);
    at = end + 1;
  }
  return part.join(", ");
}

/** The sentence the build reads. Call it only on a week {@link openingWeekProblem} accepts. */
export function openingHoursText(week: OpeningWeek): string {
  return hoursText(week, GRAMMAR_DAY_SHORT);
}

function hoursText(week: OpeningWeek, names: readonly string[]): string {
  const clause: { readonly period: readonly OpeningPeriod[]; readonly day: number[] }[] = [];
  for (const [index, period] of week.entries()) {
    if (period.length === 0) continue;
    const same = clause.find((one) => sameDay(one.period, period));
    if (same === undefined) clause.push({ period, day: [index] });
    else same.day.push(index);
  }
  return clause
    .map((one) => `${dayListOf(one.day, names)} ${one.period.map(periodText).join(", ")}`)
    .join("; ");
}

/** The week in words for the Review step: "Mon-Fri 12:00–23:30 · Sat, Sun closed". */
export function openingHoursSummary(week: OpeningWeek): string {
  const closedDay = week.flatMap((period, index) => (period.length === 0 ? [index] : []));
  const open = hoursText(week, DAY_SHORT()).replace(/;\s*/g, " · ").replaceAll("00:00-24:00", portalText("operations", "open_all_day_2aa250a6"));
  return closedDay.length === 0 ? open : portalText("operations", "value_value_closed_97a9cd40", { v0: String(open), v1: String(dayListOf(closedDay, DAY_SHORT())) });
}

/** Reads a week back from storage, or `null` when the value is not one. */
export function openingWeekOf(value: unknown): OpeningWeek | null {
  if (!Array.isArray(value) || value.length !== DAY_NAME().length) return null;
  const week: OpeningPeriod[][] = [];
  for (const day of value) {
    if (!Array.isArray(day) || day.length > MAX_SHIFT_PER_DAY) return null;
    const period: OpeningPeriod[] = [];
    for (const one of day) {
      if (typeof one !== "object" || one === null) return null;
      const { opensAt, closesAt } = one as { opensAt?: unknown; closesAt?: unknown };
      if (typeof opensAt !== "string" || typeof closesAt !== "string") return null;
      period.push({ opensAt, closesAt });
    }
    week.push(period);
  }
  return week;
}

/**
 * The week the way the Review step's "Opening hours" row writes it (design sync 2026-10-09):
 * "Tue–Thu 12:00–15:00 and 18:00–23:00 · Fri 12:00–15:00 and 18:00–23:30 · Sat 12:00–23:30" — consecutive days with
 * the same shifts share a run, closed days are left out, and a week closed every day says so.
 */
export function openingHoursLine(week: OpeningWeek): string {
  const run: { from: number; to: number; sig: string }[] = [];
  for (const [index, period] of week.entries()) {
    if (period.length === 0) continue;
    const sig = period
      .map((one) => (isAllDay(one) ? portalText("operations", "open_all_day_2aa250a6") : `${one.opensAt}–${one.closesAt}`))
      .join(portalText("operations", "opening.period.and"));
    const last = run.at(-1);
    if (last !== undefined && last.sig === sig && last.to === index - 1) last.to = index;
    else run.push({ from: index, to: index, sig });
  }
  if (run.length === 0) return portalText("operations", "closed_every_day_9654e360");
  return run
    .map((one) => {
      const from = DAY_SHORT()[one.from] as string;
      const to = DAY_SHORT()[one.to] as string;
      return `${one.from === one.to ? from : `${from}–${to}`} ${one.sig}`;
    })
    .join(" · ");
}

// ── plan-015 AGC-UI: the answering answers (G4, G8, G12; IC-DG-2) ─────────────────────────────
//
// The VoiceAgent's own time zone (owner answer 12), its special dates, and what it does when it is closed or every line
// is busy. The wizard's Hours step and the agent page's "When it answers" card edit the same answers with the rules
// below, so the two can never disagree about what a date chip says, which wait a sentence names, or why "Forward to a
// phone" cannot be chosen.

/** One special date as the editors hold it: closed all day (no periods) or open in its own periods. */
export interface SpecialDateEntry {
  /** `YYYY-MM-DD`, in the VoiceAgent's own time zone. */
  readonly date: string;
  /** An optional name ("Christmas"); `""` when unnamed — the API's "left out, none". */
  readonly label: string;
  readonly interval: readonly OpeningPeriod[];
}

const MONTH_SHORT = () => ([portalText("operations", "jan_efed3690"), portalText("operations", "feb_dc8415cc"), portalText("operations", "mar_c4ba0822"), portalText("operations", "apr_befde54a"), portalText("operations", "may_c94f4798"), portalText("operations", "jun_6d90df3b"), portalText("operations", "jul_b7375584"), portalText("operations", "aug_75629af5"), portalText("operations", "sep_fdd289e3"), portalText("operations", "oct_51327aef"), portalText("operations", "nov_bb9bfefd"), portalText("operations", "dec_997f59bc")] as const);

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TYPED_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/** `true` for a real calendar date written `YYYY-MM-DD` (no 31 February). */
export function isCalendarDateText(value: string): boolean {
  const found = ISO_DATE.exec(value);
  if (found === null) return false;
  const at = new Date(Date.UTC(Number(found[1]), Number(found[2]) - 1, Number(found[3])));
  return (
    at.getUTCFullYear() === Number(found[1]) &&
    at.getUTCMonth() === Number(found[2]) - 1 &&
    at.getUTCDate() === Number(found[3])
  );
}

/**
 * The date box's text as a calendar date: the design's `DD/MM/YYYY` (the prototype's `addHoliday`), or `YYYY-MM-DD`;
 * `null` for anything that is not a real date.
 */
export function calendarDateOfTyped(text: string): string | null {
  const trimmed = text.trim();
  const typed = TYPED_DATE.exec(trimmed);
  const iso =
    typed === null
      ? trimmed
      : `${String(typed[3])}-${String(typed[2]).padStart(2, "0")}-${String(typed[1]).padStart(2, "0")}`;
  return isCalendarDateText(iso) ? iso : null;
}

function dayAfter(date: string): string {
  const at = new Date(`${date}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + 1);
  return at.toISOString().slice(0, 10);
}

function dateParts(date: string): {
  readonly day: number;
  readonly month: string;
  readonly year: number;
} {
  const at = new Date(`${date}T12:00:00Z`);
  return {
    day: at.getUTCDate(),
    month: MONTH_SHORT()[at.getUTCMonth()] ?? "",
    year: at.getUTCFullYear(),
  };
}

/** A special date's hours in words: "Closed", or "12:00–15:00, 18:00–23:00" (the prototype's chip). */
export function specialDateHoursText(interval: readonly OpeningPeriod[]): string {
  return interval.length === 0 ? portalText("operations", "closed_88d86b77") : interval.map((one) => `${one.opensAt}–${one.closesAt}`).join(", ");
}

/** "24 Dec 2026", "24–26 Dec 2026", "30 Dec 2026–1 Jan 2027". */
function dateRunText(first: string, last: string): string {
  const from = dateParts(first);
  if (first === last) return `${String(from.day)} ${from.month} ${String(from.year)}`;
  const to = dateParts(last);
  if (from.year === to.year && from.month === to.month)
    return `${String(from.day)}–${String(to.day)} ${to.month} ${String(to.year)}`;
  if (from.year === to.year)
    return `${String(from.day)} ${from.month}–${String(to.day)} ${to.month} ${String(to.year)}`;
  return `${String(from.day)} ${from.month} ${String(from.year)}–${String(to.day)} ${to.month} ${String(to.year)}`;
}

/** One chip of the special dates: consecutive dates with the same hours and name share it ("24–26 Dec 2026 · Closed"). */
export interface SpecialDateChip {
  readonly text: string;
  /** The dates the chip stands for; its remove button removes all of them. */
  readonly date: readonly string[];
  /** The dates' name, `""` when unnamed — the chip's tooltip. */
  readonly label: string;
}

/** The chips, in date order. */
export function specialDateChipOf(entry: readonly SpecialDateEntry[]): readonly SpecialDateChip[] {
  const sorted = [...entry].sort((one, other) => one.date.localeCompare(other.date));
  const run: {
    first: string;
    last: string;
    label: string;
    hours: string;
    date: string[];
  }[] = [];
  for (const one of sorted) {
    const hours = specialDateHoursText(one.interval);
    const previous = run.at(-1);
    if (
      previous !== undefined &&
      previous.hours === hours &&
      previous.label === one.label &&
      dayAfter(previous.last) === one.date
    ) {
      previous.last = one.date;
      previous.date.push(one.date);
      continue;
    }
    run.push({
      first: one.date,
      last: one.date,
      label: one.label,
      hours,
      date: [one.date],
    });
  }
  return run.map((one) => ({
    text: `${dateRunText(one.first, one.last)} · ${one.hours}`,
    date: one.date,
    label: one.label,
  }));
}

/**
 * The Review step's line (the prototype's `specialLine`, export of 2026-10-10): every date in date order, each with its
 * hours when it is open — "15 Aug, 24 Dec 12:00–15:00, 25 Dec, 1 Jan" — or "None".
 */
export function specialDateLine(entry: readonly SpecialDateEntry[]): string {
  if (entry.length === 0) return portalText("operations", "none_6eef6648");
  return [...entry]
    .sort((one, other) => one.date.localeCompare(other.date))
    .map((one) => {
      const part = dateParts(one.date);
      const day = `${String(part.day)} ${part.month}`;
      return one.interval.length === 0 ? day : `${day} ${specialDateHoursText(one.interval)}`;
    })
    .join(", ");

}

/** "That day" in the add row: closed all day, or its own hours (the prototype's `newModes`). */
export type SpecialDateMode = "closed" | "hours";

/** What the add row holds before "Add date". */
export interface SpecialDateDraft {
  readonly typed: string;
  readonly mode: SpecialDateMode;
  readonly opensAt: string;
  readonly closesAt: string;
}

/** The prototype's starting row: no date, closed, and 12:00 to 15:00 ready for "Different hours". */
export const FRESH_SPECIAL_DATE: SpecialDateDraft = {
  typed: "",
  mode: "closed",
  opensAt: "12:00",
  closesAt: "15:00",
};

/** At most this many special dates (the API's `special_date` `maxItems`, IC-DG-2). */
export const SPECIAL_DATE_LIMIT = 60;

/**
 * The add row's date as an entry, or the sentence that says why it cannot be added. `today` is the date now in the
 * VoiceAgent's own zone: a date already past would be dropped at the build, so it is refused here instead.
 */
export function specialDateOfDraft(
  draft: SpecialDateDraft,
  existing: readonly SpecialDateEntry[],
  today: string,
): { readonly entry: SpecialDateEntry } | { readonly problem: string } {
  const date = calendarDateOfTyped(draft.typed);
  if (date === null) return { get problem() { return portalText("operations", "write_the_date_as_dd_mm_yyyy_for_example_25_12_2026_14a1ecb6"); } };
  if (date < today) return { get problem() { return portalText("operations", "that_date_has_passed_choose_today_or_a_later_date_18d52547"); } };
  const replacing = existing.some((one) => one.date === date);
  if (!replacing && existing.length >= SPECIAL_DATE_LIMIT) {
    return {
      get problem() { return portalText("operations", "at_most_value_special_dates_can_be_set_remove_some_first_8b0b3ba1", { v0: String(String(SPECIAL_DATE_LIMIT)) }); },
    };
  }
  if (draft.mode === "closed") return { entry: { date, label: "", interval: [] } };
  const period = {
    opensAt: draft.opensAt.trim(),
    closesAt: draft.closesAt.trim(),
  };
  if (!TIME.test(period.opensAt) || !TIME.test(period.closesAt)) {
    return {
      get problem() { return portalText("operations", "write_both_times_as_hh_mm_for_example_12_00_to_15_00_d4aa7b61"); },
    };
  }
  if (period.opensAt === period.closesAt && period.opensAt !== "00:00") {
    return {
      get problem() { return portalText("operations", "it_opens_and_closes_at_the_same_time_for_the_whole_day_wri_0e053675"); },
    };
  }
  return { entry: { date, label: "", interval: [period] } };
}

/** The list with `entry` added — a date already listed is replaced (the prototype's), in date order. */
export function withSpecialDate(
  existing: readonly SpecialDateEntry[],
  entry: SpecialDateEntry,
): readonly SpecialDateEntry[] {
  return [...existing.filter((one) => one.date !== entry.date), entry].sort((one, other) =>
    one.date.localeCompare(other.date),
  );
}

/** The list without the dates of one chip. */
export function withoutSpecialDate(
  existing: readonly SpecialDateEntry[],
  date: readonly string[],
): readonly SpecialDateEntry[] {
  return existing.filter((one) => !date.includes(one.date));
}

/** Two lists say the same thing (the editors' "changed?"). */
export function sameSpecialDate(one: readonly SpecialDateEntry[], other: readonly SpecialDateEntry[]): boolean {
  const key = (entry: readonly SpecialDateEntry[]): string =>
    [...entry]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((each) => `${each.date}|${each.label}|${specialDateHoursText(each.interval)}`)
      .join(";");
  return key(one) === key(other);
}

/**
 * The zones the time zone select offers: the browser's IANA list, with the one chosen kept even where the browser's
 * list omits it (a newer spelling, `UTC`), as the sign-up form's `timeZoneOptions` does.
 */
export function timeZoneOptionOf(selected: string): readonly string[] {
  const known = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  return known.includes(selected) ? known : [selected, ...known];
}

/**
 * Where a zone's clock is, in the words the export of 2026-10-10 uses for its time zone select and the agent page's
 * "Times are in Rome time (Italy)": the zone's city and, for the zones named here, its country. A zone not named here
 * is its city alone ("Abidjan time") — never a country guessed. The select's value stays the IANA name.
 */
const ZONE_COUNTRY = (): Readonly<Record<string, string>> => ({
  "Europe/Rome": portalText("operations", "italy_ad79ef0f"),
  "Europe/London": portalText("operations", "merge.wizard.country_uk"),
  "Europe/Madrid": portalText("operations", "spain_20a8df9b"),
  "Atlantic/Canary": portalText("operations", "spain_20a8df9b"),
  "Europe/Athens": portalText("operations", "greece_4902a456"),
  "Europe/Paris": portalText("operations", "france_e3772ac4"),
  "Europe/Berlin": portalText("operations", "germany_17d53e0e"),
  "Europe/Lisbon": portalText("operations", "portugal_a495190b"),
  "Atlantic/Madeira": portalText("operations", "portugal_a495190b"),
  "Atlantic/Azores": portalText("operations", "portugal_a495190b"),
  "Europe/Dublin": portalText("operations", "ireland_eb2131ec"),
  "Europe/Amsterdam": portalText("operations", "netherlands_fb61c8a8"),
  "Europe/Brussels": portalText("operations", "belgium_5cb4c9d8"),
  "Europe/Luxembourg": portalText("operations", "merge.wizard.luxembourg.507672"),
  "Europe/Vienna": portalText("operations", "merge.wizard.austria.593905"),
  "Europe/Zurich": portalText("operations", "merge.wizard.switzerland.77dcd8"),
  "Europe/Stockholm": portalText("operations", "merge.wizard.sweden.72ddd2"),
  "Europe/Copenhagen": portalText("operations", "merge.wizard.denmark.89da12"),
  "Europe/Oslo": portalText("operations", "merge.wizard.norway.988455"),
  "Europe/Helsinki": portalText("operations", "merge.wizard.finland.c909b1"),
  "Europe/Warsaw": portalText("operations", "merge.wizard.poland.5ff03b"),
  "Europe/Prague": portalText("operations", "merge.wizard.czechia.045e2f"),
  "Europe/Bratislava": portalText("operations", "merge.wizard.slovakia.b6c149"),
  "Europe/Budapest": portalText("operations", "merge.wizard.hungary.f14e46"),
  "Europe/Bucharest": portalText("operations", "merge.wizard.romania.d6b897"),
  "Europe/Sofia": portalText("operations", "merge.wizard.bulgaria.5c7772"),
  "Europe/Zagreb": portalText("operations", "merge.wizard.croatia.d7e045"),
  "Europe/Ljubljana": portalText("operations", "merge.wizard.slovenia.d1aa05"),
  "Europe/Belgrade": portalText("operations", "merge.wizard.serbia.6d31bf"),
  "Europe/Tallinn": portalText("operations", "merge.wizard.estonia.f0a96d"),
  "Europe/Riga": portalText("operations", "merge.wizard.latvia.c5f5bb"),
  "Europe/Vilnius": portalText("operations", "merge.wizard.lithuania.74a788"),
  "Europe/Malta": portalText("operations", "merge.wizard.malta.1a591a"),
  "Asia/Nicosia": portalText("operations", "cyprus_852addab"),
  "Europe/Nicosia": portalText("operations", "cyprus_852addab"),
  "Europe/Istanbul": portalText("operations", "merge.wizard.turkey.d7153e"),
  "Europe/Kyiv": portalText("operations", "merge.wizard.ukraine.c951ec"),
  "Europe/Kiev": portalText("operations", "merge.wizard.ukraine.c951ec"),
  "Asia/Jerusalem": portalText("operations", "merge.wizard.israel.4c197d"),
  "America/New_York": portalText("operations", "merge.wizard.us_east.ec7f7e"),
  "America/Chicago": portalText("operations", "merge.wizard.us_central.d66d6a"),
  "America/Denver": portalText("operations", "merge.wizard.us_mountain.99f5d1"),
  "America/Los_Angeles": portalText("operations", "merge.wizard.us_west.fa6128"),
});

export function timeZoneLabelOf(zone: string): string {
  if (zone === "UTC" || zone === "Etc/UTC") return "UTC";
  const city = (zone.split("/").at(-1) ?? zone).replaceAll("_", " ");
  const country = ZONE_COUNTRY()[zone];
  return country === undefined ? portalText("operations", "merge.wizard.v0_time.c027e5", { v0: String(city) }) : portalText("operations", "merge.wizard.v0_time_v1.75dbad", { v0: String(city), v1: String(country) });
}

// ── out of hours and every line busy ────────────────────────────────────────────────────────

export type AfterHoursChoiceCode = "closed" | "message" | "forward";
export type BusyChoiceCode = "queue" | "message" | "forward";

/** The design's longest waits, in minutes (`LONGEST_WAIT_MINUTE`, IC-DG-2). */
export const WAIT_MINUTE_CHOICE = [1, 2, 3, 5] as const;
export type WaitMinute = (typeof WAIT_MINUTE_CHOICE)[number];

/** The wait the design pre-selects (`s.maxWait || '2 min'`). */
export const DEFAULT_WAIT_MINUTE: WaitMinute = 2;

/** The choices as they are sent: both stated together, the wait only with a queue, the number only with a forward. */
export interface AnsweringChoice {
  readonly afterHoursChoice: AfterHoursChoiceCode;
  readonly busyChoice: BusyChoiceCode;
  readonly longestWaitMinute: WaitMinute | null;
  readonly forwardTo: string | null;
}

/**
 * What a new agent starts with (the wizard): say you are closed out of hours — what the platform did before these
 * answers existed — and hold callers in a queue for the design's two minutes.
 */
export const DEFAULT_ANSWERING_CHOICE: AnsweringChoice = {
  afterHoursChoice: "closed",
  busyChoice: "queue",
  longestWaitMinute: DEFAULT_WAIT_MINUTE,
  forwardTo: null,
};

/**
 * Whether an agent of this agent_template answers at every hour (owner decision 2026-10-14, ADR 0061 amendment): its
 * engine answers from the owner's information, so opening hours and special dates decide only when a caller can be put
 * through to a person — the runtime never plays the out-of-hours plan for it. Read from the catalogue's engine
 * capability (`answersOutsideOpeningHours`), never from the code itself; an unknown code or no engine answers `false`.
 */
export function answersAtAllHoursOf(agentTemplateCode: string | null): boolean {
  if (agentTemplateCode === null) return false;
  const engine = agentTemplateOf(agentTemplateCode)?.conversationEngine;
  return engine !== undefined && engine !== null && answersOutsideOpeningHours(engineCapabilityOf(engine));
}

/**
 * The choices of an agent that answers at every hour: its out-of-hours choice is never played, so a stored "forward"
 * reads (and is sent) as "take a message" — it then neither shows the forward number nor needs forwarding switched on
 * to save. The busy choice and its number are kept exactly.
 */
export function allHoursChoiceOf(choice: AnsweringChoice): AnsweringChoice {
  return choice.afterHoursChoice === "forward" ? { ...choice, afterHoursChoice: "message" } : choice;
}

export function isWaitMinute(value: number | null): value is WaitMinute {
  return value !== null && (WAIT_MINUTE_CHOICE as readonly number[]).includes(value);
}

/** Whether forwarding to a phone can be chosen, and the facts its copy states — all served by the API. */
export interface ForwardFact {
  /** `getVoiceAgent.forward_to_phone`: while `off` a forward is refused, so the choice is greyed. */
  readonly forwardToPhone: "off" | "on";
  /** How long a forward rings before the caller is asked for a message. */
  readonly ringSecond: number;
  /** The longest one forwarded call may last. */
  readonly timeLimitSecond: number;
}

/**
 * Why "Forward to a phone" cannot be chosen now (plan-015 AP-5: removed when forwarding is switched on), or `null`.
 * `null` facts — a surface the API does not tell yet — read as off: the choice is never offered on a guess.
 */
export function forwardClosedReasonOf(fact: ForwardFact | null): string | null {
  return fact !== null && fact.forwardToPhone === "on"
    ? null
    : portalText("operations", "forwarding_is_being_tested_and_will_be_available_soon_961892c2");
}

/** The Greek example number the forward copy shows (owner answer 29; forwarding is GR-only at launch). */
export const FORWARD_EXAMPLE_NUMBER = "+30 69 1234 5678";

/** Owner answer 19: calls over the customer's own SIP trunk, and website voice, take a message instead. */
export const FORWARD_SCOPE_SENTENCE =
  () => (portalText("operations", "applies_to_wetalk_numbers_only_calls_through_your_own_sip_4d5e0a25"));

/** A whole number of minutes in words ("1 minute", "2 minutes"); anything else as talk time ("1 min 30 s"). */
export function minuteText(second: number): string {
  if (second > 0 && second % 60 === 0) {
    const minute = second / 60;
    return `${String(minute)} ${minute === 1 ? portalText("operations", "minute_04987fca") : portalText("operations", "minutes_be2e2bb6")}`;
  }
  return talkTimeText(second);
}

/**
 * How much longer than the requested ring the phone really rings (forward spike 2026-10-13, gate G-B): Telnyx was asked
 * for 20 s and the carrier measured 21–30 s, so the copy promises "about 20–30 seconds", never a bare 20.
 */
export const FORWARD_RING_OVERRUN_SECOND = 10;

function ringClause(fact: ForwardFact | null): string {
  return fact === null
    ? ""
    : portalText("operations", "for_about_value_seconds_2b7c7c71", {
        v0: String(fact.ringSecond),
        v1: String(fact.ringSecond + FORWARD_RING_OVERRUN_SECOND),
      });
}

function forwardNumberText(forwardTo: string | null): string {
  return forwardTo === null || forwardTo.trim() === ""
    ? portalText("operations", "the_number_you_give_for_example_value_594d1d82", { v0: String(FORWARD_EXAMPLE_NUMBER) })
    : forwardTo;
}

export interface AnsweringOption<T extends string> {
  readonly choice: T;
  readonly label: string;
  readonly dek: string;
}

/** Out of hours: the design's three choices, their copy made true by what is served. */
export function afterHoursOptionOf(
  forwardTo: string | null,
  fact: ForwardFact | null,
): readonly AnsweringOption<AfterHoursChoiceCode>[] {
  return [
    {
      choice: "closed",
      label: portalText("operations", "say_you_are_closed_f2b3c381"),
      dek: portalText("operations", "tells_the_caller_when_you_next_open_then_ends_the_call_ffb9b218"),
    },
    {
      choice: "message",
      label: portalText("operations", "take_a_message_434aed00"),
      // Owner answer 28: the design's "You get it by email" is kept, though no email is sent until mail is switched on.
      dek: portalText("operations", "says_when_you_next_open_then_takes_a_name_a_number_and_wha_03824278"),
    },
    {
      choice: "forward",
      label: portalText("operations", "forward_to_a_phone_22b04382"),
      dek: portalText("operations", "rings_valuevalue_then_takes_a_message_if_nobody_picks_up_v_b8abeddc", { v0: String(forwardNumberText(forwardTo)), v1: String(ringClause(fact)), v2: String(FORWARD_SCOPE_SENTENCE()) }),
    },
  ];
}

/** Every line busy: the design's three choices; the queue's copy is what callers really hear (owner answer 13). */
export function busyOptionOf(
  forwardTo: string | null,
  fact: ForwardFact | null,
): readonly AnsweringOption<BusyChoiceCode>[] {
  return [
    {
      choice: "queue",
      label: portalText("operations", "hold_them_in_a_queue_0837192f"),
      dek: portalText("operations", "callers_hear_that_every_line_is_busy_and_are_told_again_at_08a07e27"),
    },
    {
      choice: "message",
      label: portalText("operations", "take_a_message_instead_cf24e6de"),
      dek: portalText("operations", "no_waiting_the_agent_takes_a_number_and_you_call_back_0cc581a4"),
    },
    {
      choice: "forward",
      label: portalText("operations", "forward_to_a_phone_22b04382"),
      dek: portalText("operations", "rings_valuevalue_so_a_person_can_answer_then_takes_a_messa_5e9e3796", { v0: String(forwardNumberText(forwardTo)), v1: String(ringClause(fact)), v2: String(FORWARD_SCOPE_SENTENCE()) }),
    },
  ];
}

/** The note under "Longest wait" (the design's `waitNote`), from the wait callers are really given. */
export function waitNoteOf(waitSecond: number): string {
  return portalText("operations", "callers_hear_all_our_lines_are_busy_right_now_please_stay_3ec50f42", { v0: String(minuteText(waitSecond)) });
}

/**
 * The Channels step's sentence (the design's, G8): what a caller hears when every line is busy, its minutes from the
 * wait the build will use — never a fixed "2 minutes".
 */
export function busyLineSentenceOf(choice: AnsweringChoice): string {
  if (choice.busyChoice === "queue" && choice.longestWaitMinute !== null) {
    const wait = minuteText(choice.longestWaitMinute * 60);
    return portalText("operations", "when_every_line_is_busy_callers_hear_all_our_lines_are_bus_82b50f6f", { v0: String(wait), v1: String(wait) });
  }
  if (choice.busyChoice === "forward") {
    return portalText("operations", "when_every_line_is_busy_the_call_rings_your_phone_so_a_per_61d9470c");
  }
  return portalText("operations", "when_every_line_is_busy_callers_do_not_wait_the_agent_take_48f1341c");
}

/** The forward's time limit (owner answer 25), as the number field's note says it. */
export function forwardLimitSentenceOf(fact: ForwardFact | null): string | null {
  return fact === null
    ? null
    : portalText("operations", "a_forwarded_call_ends_after_value_talk_time_is_billed_unti_5640e357", { v0: String(minuteText(fact.timeLimitSecond)) });
}

/** The forward number as it is sent: spaces, dashes and brackets dropped (`+30 69 1234 5678` → `+306912345678`). */
export function forwardToOf(typed: string): string {
  return typed.replace(/[\s\-().]/g, "");
}

/**
 * What is wrong with the forward number, in the API's own words (the reviewed destination rule, ADR 0047), or `null`.
 * Whether the number answers through WeTalk is the API's to say when it is saved.
 */
export function forwardNumberProblemOf(typed: string): string | null {
  const e164 = forwardToOf(typed);
  if (e164 === "") return portalText("operations", "enter_the_phone_to_forward_to_for_example_value_6843afc2", { v0: String(FORWARD_EXAMPLE_NUMBER) });
  const refused = forwardDestinationRefusalOf(e164);
  if (refused === null) return null;
  switch (refused) {
    case "not_e164": return portalText("operations", "forward.number.invalid");
    case "country_unreviewed":
    case "country_not_allowed": return portalText("operations", "forward.number.country");
    case "premium_rate": return portalText("operations", "forward.number.premium");
    case "emergency_or_short": return portalText("operations", "forward.number.emergency");
  }
}

/** Whether a choice set forwards, so a number is needed. */
export function forwards(choice: AnsweringChoice): boolean {
  return choice.afterHoursChoice === "forward" || choice.busyChoice === "forward";
}

/** The choices as they are sent: the wait only with a queue (pre-selected at two), the number only with a forward. */
export function sentChoiceOf(choice: AnsweringChoice): AnsweringChoice {
  return {
    afterHoursChoice: choice.afterHoursChoice,
    busyChoice: choice.busyChoice,
    longestWaitMinute: choice.busyChoice === "queue" ? (choice.longestWaitMinute ?? DEFAULT_WAIT_MINUTE) : null,
    forwardTo: forwards(choice) && choice.forwardTo !== null ? forwardToOf(choice.forwardTo) : null,
  };
}

/** What stops the choices being saved: a forward while forwarding is closed, or a forward number that is refused. */
export function answeringChoiceProblemOf(choice: AnsweringChoice, fact: ForwardFact | null): string | null {
  if (!forwards(choice)) return null;
  const closed = forwardClosedReasonOf(fact);
  if (closed !== null) return closed;
  return forwardNumberProblemOf(choice.forwardTo ?? "");
}

export function sameAnsweringChoice(one: AnsweringChoice | null, other: AnsweringChoice | null): boolean {
  if (one === null || other === null) return one === other;
  const a = sentChoiceOf(one);
  const b = sentChoiceOf(other);
  return (
    a.afterHoursChoice === b.afterHoursChoice &&
    a.busyChoice === b.busyChoice &&
    a.longestWaitMinute === b.longestWaitMinute &&
    a.forwardTo === b.forwardTo
  );
}
